import React, { useEffect, useState, useMemo } from "react";
import Dashboard from "../components/Dashboard";
import Pagination from "../components/Pagination";
import config from "../config";
import ResolveSidebar, {
  triggerAgent1,
  mapDbToAgent1State,
  mapDbToAgent2State,
} from "../components/ResolveSidebar";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  FioriSwitch,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
} from "../components/FioriUI";

const url = config.api.mtcExceptions;

function M2CExceptions() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    exceptionType: "",
    search: "",
    customer: "",
    processedTime: "",
  });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // ── Sidebar state ──────────────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarRow,  setSidebarRow]  = useState(null);

  // ── Agent state — plain useState, no sessionStorage ───────────────────────
  // All display data comes from DB. Page refresh resets to IDLE; user re-opens
  // the panel and the DB row already has the Agent 1/2 results if they ran.
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [agentPhase,   setAgentPhase]   = useState("idle");
  const [agent1Result, setAgent1Result] = useState(null);
  const [agent2Result, setAgent2Result] = useState(null);

  const [exceptionTypeList, setExceptionTypeList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const isDashboard = view === "dashboard";
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.records || [];
        setData(apiData);
        setLoading(false);
        setExceptionTypeList(
          Array.from(new Set(apiData.map((i) => i.case_type).filter(Boolean))),
        );
        setStatusList(
          Array.from(new Set(apiData.map((i) => i.status).filter(Boolean)))
            .filter((s) => s.toUpperCase() !== "ESCALATED"),
        );
      })
      .catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (filters.status) {
      filtered = filtered.filter(
        (i) => i.status?.toUpperCase() === filters.status.toUpperCase(),
      );
    }
    if (filters.exceptionType) {
      filtered = filtered.filter((i) => i.case_type === filters.exceptionType);
    }
    if (filters.customer) {
      filtered = filtered.filter((i) => i.provider_name === filters.customer);
    }
    if (filters.processedTime) {
      const now = new Date();
      filtered = filtered.filter((i) => {
        if (!i.completed_at) return false;
        const hoursDiff = (now - new Date(i.completed_at)) / (1000 * 60 * 60);
        if (filters.processedTime === "Last Day")   return hoursDiff <= 24;
        if (filters.processedTime === "Last Week")  return hoursDiff <= 24 * 7;
        if (filters.processedTime === "Last Month") return hoursDiff <= 24 * 30;
        return true;
      });
    }
    if (filters.search) {
      const words = filters.search.toLowerCase().trim().split(" ");
      filtered = filtered.filter((i) => {
        const combined =
          `${i.case_id || ""} ${i.device_id || ""} ${i.status || ""} ${i.case_type || ""} ${i.scenario_detected || ""}`.toLowerCase();
        return words.every((w) => combined.includes(w));
      });
    }
    return filtered;
  }, [data, filters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Table data excludes ESCALATED (only used in dashboard charts)
  const tableData = useMemo(() => {
    return filteredData.filter(
      (i) => (i.status || "").toUpperCase() !== "ESCALATED",
    );
  }, [filteredData]);

  const totalPages = Math.ceil(tableData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return tableData.slice(start, start + ITEMS_PER_PAGE);
  }, [tableData, currentPage]);

  const toggleRow = (rowId) =>
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));

  const formatScenario = (val) =>
    !val
      ? "-"
      : val
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());

  const renderActionDetails = (agentActions) => {
    if (!agentActions)
      return <span className="text-fiori-text-disabled">-</span>;

    let parts = [];

    if (Array.isArray(agentActions)) {
      parts = agentActions
        .map((l) => (typeof l === "string" ? l : JSON.stringify(l)))
        .map((s) => s.replace(/^[•\-]\s*/, "").trim())
        .filter((s) => s.length > 0);
    } else if (typeof agentActions === "string") {
      parts = agentActions
        .split(/[•\n]/)
        .map((s) => s.replace(/^-\s*/, "").trim())
        .filter((s) => s.length > 0);
    } else {
      return <span className="text-fiori-text-disabled">-</span>;
    }

    if (parts.length === 0)
      return <span className="text-fiori-text-disabled">-</span>;

    const waitingRegex = /waiting for/i;
    return (
      <div className="space-y-2">
        {parts.map((line, idx) => {
          const isWaiting = waitingRegex.test(line);
          return (
            <div
              key={idx}
              className={`text-sm leading-snug ${isWaiting ? "text-fiori-warning font-medium" : "text-fiori-success font-medium"}`}
            >
              • {line}
            </div>
          );
        })}
      </div>
    );
  };

  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const handleClearFilters = () =>
    setFilters({ status: "", exceptionType: "", search: "", customer: "", processedTime: "" });

  const handleRefreshData = () => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        const apiData = json.records || [];
        setData(apiData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // Open sidebar via panel icon.
  // The row already contains all new columns from the Lambda GET on page load.
  // If Agent 1 has run (agent_run_id present), restore state immediately from
  // that data — no extra fetch needed, no loading spinner shown.
  const openSidebar = (row) => {
    setSidebarRow(row);
    setSidebarOpen(true);

    if (!row.agent_run_id) {
      // Agent 1 has never run for this case — show IDLE
      setActiveCaseId(row.case_id);
      setAgentPhase("idle");
      setAgent1Result(null);
      setAgent2Result(null);
      return;
    }

    // Agent 1 has run — restore from row data
    setActiveCaseId(row.case_id);
    setAgent1Result(mapDbToAgent1State(row));

    const agent2Mode = (row.agent2_mode || "").toLowerCase();
    const status     = (row.status     || "").toUpperCase();

    if (status === "RESOLVED" || agent2Mode === "resolved") {
      setAgentPhase("agent2_resolved");
      setAgent2Result(mapDbToAgent2State(row));
    } else if (status === "REJECTED" || agent2Mode === "rejected") {
      setAgentPhase("agent2_rejected");
      setAgent2Result(mapDbToAgent2State(row));
    } else if (agent2Mode === "preview") {
      setAgentPhase("agent2_preview");
      setAgent2Result(mapDbToAgent2State(row));
    } else if (["no_reply", "reminder", "escalated"].includes(agent2Mode)) {
      // Reminder and escalated also land on no_reply UI — user can Check Again
      setAgentPhase("agent2_no_reply");
      setAgent2Result(mapDbToAgent2State(row));
    } else {
      // Agent 1 ran, Agent 2 hasn't been invoked yet
      setAgentPhase("agent1_done");
      setAgent2Result(null);
    }
  };

  // Resolve button — triggers Agent 1, opens sidebar
  const handleResolve = async (row) => {
    const id = row.case_id;
    // Reset agent state for this case
    setActiveCaseId(id);
    setAgentPhase("agent1_loading");
    setAgent1Result(null);
    setAgent2Result(null);
    // Optimistically grey out the Resolve button immediately
    setData((prev) => prev.map((i) => i.case_id === id ? { ...i, status: "IN_PROGRESS" } : i));
    setSidebarRow({ ...row, status: "IN_PROGRESS" });
    setSidebarOpen(true);
    // Trigger Agent 1 — passes lambdaUrl so it can fetch from DB after polling
    try {
      const result = await triggerAgent1(id, url);
      setAgent1Result(result);
      setAgentPhase("agent1_done");
    } catch (err) {
      setAgentPhase("agent1_error");
      console.error("Agent 1 failed:", err);
    }
  };

  // Called by ResolveSidebar when a final decision is made
  const handleStatusChange = (caseId, newStatus) => {
    setData((prev) =>
      prev.map((i) => i.case_id === caseId ? { ...i, status: newStatus } : i)
    );
  };

  const handleWarningClose = () => setShowWarning(false);

  return (
    <>
      <ObjectPageHeader
        title="Meter to Cash Exceptions"
        subtitle="Field cases requiring billing reconciliation"
        actions={
          <>
            <FioriSwitch
              checked={isDashboard}
              onChange={(v) => setView(v ? "dashboard" : "")}
              label="Dashboard"
            />
            <button
              type="button"
              onClick={() => window.open("/sop-editor?doc=m2c", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-fiori-border-dark rounded-fiori text-fiori-brand bg-transparent hover:bg-fiori-brand/5 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 256 256"
                fill="currentColor"
                className="w-7 h-7"
              >
                <g transform="translate(1.407 1.407) scale(2.81 2.81)">
                  <path d="M24.731 57.127v-4.129c0-.444-.361-.806-.806-.806H20.05v5.74h3.876c.444 0 .805-.362.805-.805z"/>
                  <path d="M39.841 63.508V53.286c0-.604-.491-1.094-1.094-1.094H35.16v12.41h3.587c.604 0 1.094-.492 1.094-1.094z"/>
                  <path d="M81.979 17.233L66.215 1.469A5.013 5.013 0 0062.67 0H20.59c-2.764 0-5.013 2.249-5.013 5.013v36.168h-4.28c-2.617 0-4.745 2.084-4.745 4.646v25.139c0 2.563 2.128 4.646 4.745 4.646h4.28v9.374c0 2.764 2.249 5.013 5.013 5.013h57.844c2.764 0 5.013-2.249 5.013-5.013v-64.21a5.013 5.013 0 00-1.468-3.543zM18.05 68.603c-1.104 0-2-.896-2-2v-6.67v-9.74c0-1.104.896-2 2-2h5.876c2.65 0 4.806 2.156 4.806 4.806v4.129c0 2.649-2.156 4.806-4.806 4.806H20.05v4.67c0 1.103-.896 1.999-2 1.999zM79.447 84.987c0 .559-.454 1.013-1.013 1.013H20.59c-.559 0-1.013-.454-1.013-1.013v-9.374h44.425c2.616 0 4.745-2.084 4.745-4.646V45.828c0-2.562-2.129-4.646-4.745-4.646H19.578V5.013C19.578 4.454 20.032 4 20.59 4h41.328v12.784c0 2.617 2.129 4.745 4.745 4.745h12.784v63.458zM31.16 66.603v-16.41c0-1.104.896-2 2-2h5.587c2.809 0 5.094 2.285 5.094 5.094v10.222c0 2.81-2.285 5.095-5.094 5.095H33.16c-1.105 0-2-.896-2-2zM54.255 56.397c1.104 0 2 .896 2 2s-.896 2-2 2h-3.686v6.205c0 1.104-.896 2-2 2s-2-.896-2-2v-16.41c0-1.104.896-2 2-2h8.682c1.104 0 2 .896 2 2s-.896 2-2 2h-6.682v4.205h3.686z"/>
                </g>
              </svg>
              SOP
            </button>

            <FioriButton
              variant="emphasized"
              onClick={handleRefreshData}
              disabled={loading}
              loading={loading}
            >
              {loading ? "Refreshing" : "Refresh"}
            </FioriButton>
          </>
        }
      />

      <PageContainer>
        {/* KPI cards */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {(() => {
              const totalCases = tableData.length;
              const resolvedCases = tableData.filter(
                (i) => (i.status || "").toUpperCase() === "RESOLVED"
              ).length;
              const uniqueScenarios = new Set(
                tableData.map((i) => i.case_type).filter(Boolean)
              ).size;
              const aiAccuracy =
                totalCases > 0
                  ? ((resolvedCases / totalCases) * 100).toFixed(1)
                  : "0.0";

              const cards = [
                { label: "TOTAL CASES",        value: totalCases },
                { label: "RESOLVED",           value: resolvedCases,
                  subtitle: `${totalCases > 0 ? ((resolvedCases / totalCases) * 100).toFixed(1) : "0"}% resolution rate` },
                { label: "EXCEPTIONS",         value: totalCases - resolvedCases },
                { label: "SCENARIOS DETECTED", value: uniqueScenarios },
                { label: "AI ACCURACY",        value: `${aiAccuracy}%` },
              ];

              return cards.map((card) => (
                <div key={card.label} className="bg-white rounded-lg border border-gray-200 shadow-sm px-5 py-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900 leading-tight">{card.value}</p>
                  {card.subtitle && <p className="text-xs text-gray-400 mt-1">{card.subtitle}</p>}
                </div>
              ));
            })()}
          </div>
        )}

        <FilterBar
          title="Filter Cases"
          onClear={handleClearFilters}
          footer={
            <div className="border-t border-fiori-border-light pt-3 flex flex-wrap items-center gap-3">
              <div className="w-48">
                <Field label="Email Report To" htmlFor="m2c-email">
                  <input
                    id="m2c-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    className="fiori-input"
                  />
                </Field>
              </div>
              <div className="pt-5">
                <FioriSwitch
                  checked={enabled}
                  onChange={setEnabled}
                  label={enabled ? "Email ON" : "Email OFF"}
                />
              </div>
            </div>
          }
        >
          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusList.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Exception Type" htmlFor="caseType">
            <Select
              id="caseType"
              value={filters.exceptionType}
              onChange={(e) => handleFilterChange("exceptionType", e.target.value)}
            >
              <option value="">All Exception Types</option>
              {exceptionTypeList.map((type) => (
                <option key={type} value={type}>{formatScenario(type)}</option>
              ))}
            </Select>
          </Field>

          <Field label="Processed Time" htmlFor="processedTime">
            <Select
              id="processedTime"
              value={filters.processedTime}
              onChange={(e) => handleFilterChange("processedTime", e.target.value)}
            >
              <option value="">All</option>
              <option value="Last Day">Last Day</option>
              <option value="Last Week">Last Week</option>
              <option value="Last Month">Last Month</option>
            </Select>
          </Field>

          <Field label="Search" htmlFor="m2c-search">
            <SearchInput
              id="m2c-search"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search Invoices..."
            />
          </Field>
        </FilterBar>

        {isDashboard ? (
          <Dashboard filteredData={filteredData} source="m2c" />
        ) : (
          <>
            <div className="flex justify-end mb-3">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
            <ResponsiveTable>
              <thead>
                <tr className="!bg-fiori-brand">
                  <th className="whitespace-nowrap !bg-fiori-brand !text-white">Case ID</th>
                  <th className="whitespace-nowrap !bg-fiori-brand !text-white">Exception Type</th>
                  <th className="whitespace-nowrap !bg-fiori-brand !text-white">Device ID</th>
                  <th className="!bg-fiori-brand !text-white">Status</th>
                  <th className="!bg-fiori-brand !text-white">Scenario Detected</th>
                  <th className="!bg-fiori-brand !text-white">AI Actions</th>
                  <th className="text-center whitespace-nowrap !bg-fiori-brand !text-white">Details</th>
                  <th className="text-center whitespace-nowrap !bg-fiori-brand !text-white">Action History</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="flex justify-center items-center gap-3 py-8 text-fiori-text-secondary text-base">
                        <Spinner /> Loading cases
                      </div>
                    </td>
                  </tr>
                ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState title="No cases" description="No items match the current filters." />
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, idx) => (
                    <tr key={idx} className="border-b border-fiori-border-light">
                      <td className="font-semibold text-base whitespace-nowrap py-5 px-6">
                        {row.case_id || "-"}
                      </td>
                      <td className="whitespace-nowrap text-base py-5 px-6">
                        {formatScenario(row.case_type)}
                      </td>
                      <td className="font-mono text-base whitespace-nowrap py-5 px-6">
                        {row.device_id || "-"}
                      </td>
                      <td className="py-5 px-6">
                        <StatusPill status={row.status} />
                      </td>
                      <td className="text-base py-5 px-6 max-w-[140px]">
                        {formatScenario(row.scenario_detected)}
                      </td>
                      <td className="py-5 px-6 min-w-[280px]">
                        {row.agent_actions ? (
                          <div className="border border-fiori-border-light rounded-fiori p-2 bg-fiori-page-2/40 max-w-md">
                            <div className={!expandedRows[row.case_id] ? "overflow-hidden max-h-[42px]" : ""}>
                              {renderActionDetails(row.agent_actions)}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleRow(row.case_id)}
                              className="text-sm font-semibold text-fiori-brand hover:underline mt-1"
                            >
                              {expandedRows[row.case_id] ? "Show less" : "Show more"}
                            </button>
                          </div>
                        ) : (
                          <span className="text-fiori-text-disabled">-</span>
                        )}
                      </td>
                      <td className="text-center py-5 px-6">
                        <IconButton title="View Details" onClick={() => openSidebar(row)}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </IconButton>
                      </td>
                      <td className="text-center py-5 px-6">
                        {(() => {
                          const st = (row.status || "").toUpperCase();
                          if (st === "OPEN" || st === "INITIATED") {
                            return (
                              <FioriButton variant="emphasized" onClick={() => handleResolve(row)}>
                                Resolve
                              </FioriButton>
                            );
                          }
                          if (st === "RESOLVED") {
                            return (
                              <button disabled className="px-4 py-1.5 text-sm font-semibold rounded-lg border cursor-not-allowed bg-fiori-success-bg text-fiori-success border-fiori-success-border">
                                ✓ Resolved
                              </button>
                            );
                          }
                          if (st === "REJECTED") {
                            return (
                              <button disabled className="px-4 py-1.5 text-sm font-semibold rounded-lg border cursor-not-allowed bg-fiori-error-bg text-fiori-error border-fiori-error-border">
                                ✕ Rejected
                              </button>
                            );
                          }
                          if (["IN_PROGRESS", "ESCALATED"].includes(st)) {
                            return (
                              <button disabled className="px-4 py-1.5 text-sm font-semibold rounded-lg border cursor-not-allowed bg-fiori-info-bg text-fiori-info border-fiori-info-border">
                                In Progress
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </ResponsiveTable>
          </>
        )}
      </PageContainer>

      {/* Confirmation dialog */}
      <Dialog
        show={showWarning}
        title="Successfully Initiated"
        size="sm"
        onClose={handleWarningClose}
        footer={
          <FioriButton variant="emphasized" onClick={handleWarningClose}>
            OK
          </FioriButton>
        }
      >
        <div className="px-4 py-5 text-sm text-fiori-text">
          {warningMessage}
        </div>
      </Dialog>

      {/* ResolveSidebar — always mounted, slides in/out.
          lambdaUrl reuses the same `url` constant already in this file.
          Agent state is plain useState — DB is the source of truth. */}
      <ResolveSidebar
        lambdaUrl={url}
        rowData={sidebarRow}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onStatusChange={handleStatusChange}
        activeCaseId={activeCaseId}
        phase={agentPhase}
        setPhase={setAgentPhase}
        agent1={agent1Result}
        setAgent1={setAgent1Result}
        agent2={agent2Result}
        setAgent2={setAgent2Result}
      />
    </>
  );
}

export default M2CExceptions;